/**
 * Intro Section Component for Brand Introduction and Value Proposition
 * 
 * A versatile introduction section that combines rich content, imagery,
 * and calls-to-action to introduce visitors to the WoodAlps brand,
 * philosophy, and product offerings. Features flexible content structure from CMS.
 * 
 * Content Features:
 * - Dynamic content from Sanity CMS with rich text support
 * - Flexible image positioning (left/right layouts)
 * - Brand messaging and value proposition display
 * - Call-to-action integration with custom buttons
 * - Responsive typography with brand font support
 * 
 * Layout Flexibility:
 * - Two-column layout with image and content sections
 * - Responsive stacking on mobile devices
 * - Configurable image positioning (left or right)
 * - Adaptive spacing and visual hierarchy
 * - Professional section styling and constraints
 * 
 * Rich Content Support:
 * - Portable Text rendering with custom components
 * - Brand font integration (Neuropol, Montserrat)
 * - Title-specific rendering without paragraph wrappers
 * - Flexible content structure from CMS
 * - Fallback content for missing data
 * 
 * Image Management:
 * - Dynamic image loading from Sanity CMS
 * - Responsive image sizing and optimization
 * - Professional image presentation
 * - Error handling for missing images
 * - Next.js Image component integration
 * 
 * Brand Integration:
 * - Consistent brand messaging framework
 * - Typography hierarchy with custom fonts
 * - Color scheme compliance
 * - Professional spacing and layout
 * - Brand voice and tone consistency
 * 
 * User Experience:
 * - Clear value proposition communication
 * - Engaging visual content presentation
 * - Smooth responsive transitions
 * - Accessible content structure
 * - Intuitive navigation flow
 * 
 * CMS Integration:
 * - Flexible content management from Sanity
 * - Rich text editing capabilities
 * - Dynamic image and button management
 * - Content versioning and preview support
 * - Editor-friendly content structure
 * 
 * @param {object} data - Section content data from Sanity CMS
 * @param {string|Array} data.title - Section title (string or Portable Text)
 * @param {string|Array} data.content - Main content (string or Portable Text)
 * @param {object} data.image - Section image from Sanity
 * @param {string} data.imagePosition - Image layout position ('left' or 'right')
 * @param {object} data.button - Call-to-action button configuration
 * 
 * @description Flexible brand introduction section with rich content and responsive design
 */

import Image from 'next/image';
import Link from 'next/link';
import { } from '../../src/app/studio/lib/sanity';  
import { urlForImage } from '../../src/app/studio/lib/image';
import { sanityLoader } from '../../utils/sanityLoader';
import { PortableText } from '@portabletext/react';
import { useTranslation } from '../../src/hooks/useTranslation';

/**
 * Custom components for Portable Text rendering
 * Provides brand-consistent typography and formatting
 */
const portableTextComponents = {
  marks: {
    neuropol: ({children}) => <span className="font-neuropol">{children}</span>,
    montserrat: ({children}) => <span className="font-montserrat">{children}</span>,
    strong: ({children}) => <strong>{children}</strong>,
    em: ({children}) => <em>{children}</em>,
  },
  block: {
    normal: ({children}) => <p className="mb-4 last:mb-0">{children}</p>,
    // Custom handling for title blocks to render them without paragraph tags
    h2: ({children}) => <>{children}</>,
  },
};

/**
 * Title-specific PortableText components without paragraph wrappers
 * Optimized for heading and title rendering
 */
const titlePortableTextComponents = {
  ...portableTextComponents,
  block: {
    ...portableTextComponents.block,
    normal: ({children}) => <>{children}</>,
  },
};

// Helper function to render portable text or fallback to string
const renderPortableText = (portableText, fallbackText) => {
  // Check if portableText exists, is an array, has content, and that content has text
  if (portableText && 
      Array.isArray(portableText) && 
      portableText.length > 0 && 
      portableText.some(block => 
        block.children && 
        block.children.length > 0 && 
        block.children.some(child => child.text && child.text.trim().length > 0)
      )
  ) {
    return <PortableText value={portableText} components={portableTextComponents} />;
  }
  // Return fallback text if any of the above conditions aren't met
  return <p>{fallbackText}</p>;
};

// Helper function to render title as portable text
const renderTitlePortableText = (portableText, fallbackText) => {
  // Check if portableText exists and is an array
  if (portableText && Array.isArray(portableText) && portableText.length > 0) {
    return <PortableText value={portableText} components={titlePortableTextComponents} />;
  }
  // Return fallback text if it's not a valid portable text array
  return fallbackText;
};

const IntroSection = ({ data }) => {
  const { t } = useTranslation();
  // Header height is expressed as fixed pixels to match the navbar
  const HEADER_HEIGHT = '100px'; // Combined header (70px) + nav (30px)

  return (
    <section className="overflow-hidden">
      {/* Main Title and Subsection 1 with background image */}
      <div
        className="relative w-full"
        style={{ height: `calc(100vh - ${HEADER_HEIGHT})` }}
      >
        {/* Background Image */}
        <div className="absolute inset-0">
          <Image
            src={data?.section1?.image ? urlForImage(data.section1.image) : "/images/bike-forest.jpg"}
            alt={data?.section1?.image?.alt || "Wooden bike in forest"}
            fill
            loader={sanityLoader}
            className="object-cover"
            sizes="100vw"
            priority
          />
          <div className="absolute inset-0 bg-black/40"></div>
        </div>

        {/* Text Content - Positioned on right */}
        <div className="relative h-full container-custom mx-auto flex justify-end items-center">
          <div className="w-full max-w-[450px] text-white p-3 sm:p-4 md:p-6 lg:p-8 rounded-xl">
            <h2 className="text-lg sm:text-xl md:text-2xl lg:text-3xl text-white font-serif mb-2 sm:mb-4 text-center font-neuropol flex flex-wrap justify-center">
              {renderTitlePortableText(data?.section1?.title, "Love Wood, Ride Wood")}
            </h2>
            <div className="w-16 sm:w-20 md:w-24 h-0.5 bg-accent-color my-2 sm:my-3 md:my-4 mx-auto"></div>
            
            <div className="text-white/90 leading-relaxed text-xs sm:text-sm md:text-base lg:text-lg text-justify">
              {renderPortableText(data?.section1?.description1, "We don't just build bicycles—we craft experiences.")}
            </div>
            <div className="h-1 sm:h-2 md:h-4"></div>
            <div className="text-white/90 leading-relaxed text-xs sm:text-sm md:text-base lg:text-lg text-justify">
              {renderPortableText(data?.section1?.description2, "Our wooden bikes are a perfect fusion of dynamic design, high performance, noble materials, and lasting reliability. Inspired by the breathtaking landscapes of the French Alps, each frame is meticulously designed to offer a ride like no other—one that connects you to the road and to nature itself.")}
            </div>
          </div>
        </div>
      </div>

      {/* Subsection 2 */}
      <div
        className="relative w-full"
        style={{ height: `calc(100vh - ${HEADER_HEIGHT})` }}
      >
        <div className="absolute inset-0">
          <Image
            src={data?.section2?.image ? urlForImage(data.section2.image) : "/images/wood-crafting.jpeg"}
            alt={data?.section2?.image?.alt || "Wood craftsmanship"}
            fill
            loader={sanityLoader}
            className="object-cover"
            sizes="100vw"
          />
          <div className="absolute inset-0 bg-black/60"></div>
        </div>

        <div className="relative h-full container-custom mx-auto flex justify-start items-center">
          <div className="w-full max-w-[450px] text-white p-3 sm:p-4 md:p-6 lg:p-8 rounded-xl">
            <h2 className="text-lg sm:text-xl md:text-2xl lg:text-3xl text-white font-serif mb-2 sm:mb-4 text-center font-neuropol">
              {data?.section2?.title || "The Perfect Blend of Performance and Feeling"}
            </h2>
            <div className="w-16 sm:w-20 md:w-24 h-0.5 bg-accent-color my-2 sm:my-3 md:my-4 mx-auto"></div>
            <div className="text-white/90 leading-relaxed text-xs sm:text-sm md:text-base lg:text-lg text-justify">
              {renderPortableText(data?.section2?.description1, "Performance is important, but so is the feeling of the ride. You want something special crafted with care from sustainable, high-quality materials. Our WOODALPS bikes aren't just about style; they deliver a smooth, responsive ride that's truly one of a kind.")}
            </div>
            <div className="h-2 sm:h-3 md:h-4 lg:h-6"></div>
            <div className="text-white/90 leading-relaxed text-xs sm:text-sm md:text-base lg:text-lg text-justify">
              {renderPortableText(data?.section2?.description2, "Experience the perfect blend of innovation, elegance, and craftsmanship made for those who seek more than the ordinary.")}
            </div>
          </div>
        </div>
      </div>

      {/* Subsection 3 - Completely redesigned for better responsiveness */}
      <div
        className="relative w-full"
        style={{ height: `calc(100vh - ${HEADER_HEIGHT})` }}
      >
        {/* Background Image */}
        <div className="absolute inset-0">
          <Image
            src={data?.section3?.image ? urlForImage(data.section3.image) : "/images/craftImageHome.jpg"}
            alt={data?.section3?.image?.alt || "Craftsmanship"}
            fill
            loader={sanityLoader}
            className="object-cover"
            sizes="100vw"
          />
          <div className="absolute inset-0 bg-black/50"></div>
        </div>
        
        {/* Content wrapper - Using flex layout for better control */}
        <div className="relative h-full container-custom mx-auto flex flex-col justify-between py-8 sm:py-12 md:py-16">
          {/* Top content */}
          <div className="w-full max-w-[400px] sm:max-w-[420px] md:max-w-[450px] text-white p-3 sm:p-4 md:p-6 rounded-xl">
            <h2 className="text-base sm:text-lg md:text-2xl lg:text-3xl text-white font-serif mb-2 md:mb-4 text-center font-neuropol">
              <span className="block">{data?.section3?.title || "Advanced Engineering \n & \n Craftsmanship"}</span>
            </h2>
            <div className="w-12 sm:w-16 md:w-24 h-0.5 bg-accent-color my-2 md:my-4 mx-auto"></div>
            <div className="text-white/90 leading-relaxed text-xs sm:text-sm md:text-base lg:text-lg text-justify">
              {renderPortableText(data?.section3?.description1, "Each WOODALPS frame is crafted from precision-machined wooden components using advanced CNC technology. Its hollow tube structure optimizes weight, strength, and impact resistance, while allowing internal cable routing for a sleek, functional design.")}
            </div>
          </div>
          
          {/* Bottom content */}
          <div className="flex flex-col items-end">
            <div className="w-full max-w-[400px] sm:max-w-[420px] md:max-w-[450px] text-white p-3 sm:p-4 md:p-6 rounded-xl">
              <div className="text-white/90 leading-relaxed text-xs sm:text-sm md:text-base lg:text-lg text-justify mb-3 sm:mb-4 md:mb-6">
                {renderPortableText(data?.section3?.description2, "Handcrafted near Évian-les-Bains, our bikes blend traditional woodworking with modern engineering. Made from sustainably sourced ash wood and reinforced with carbon fiber at key stress points, they offer a ride that's both stunning and unmatched in performance.")}
              </div>
              <div className="flex justify-center">
                <Link href="/design-and-engineering"
                  className="inline-block px-3 sm:px-4 md:px-6 lg:px-8 py-1.5 sm:py-2 md:py-3 bg-transparent text-white text-xs sm:text-sm md:text-base font-medium rounded-md border border-white hover:bg-accent-color hover:border-accent-color transition-all font-neuropol">
                  {t('buttons.discover_our_engineering')}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default IntroSection;
